import { chipClasses, type ChipProps } from "@mui/material/Chip";
import { alpha, type Theme } from "@mui/material/styles";

const COLORS = ["primary", "secondary", "info", "success", "warning", "error"] as const;

type PaletteColorKey = (typeof COLORS)[number];

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}

const defaultSoft = { border: "none", padding: 6, borderRadius: 8 };

const getPaletteColor = (color: string, theme: Theme): object => {
  const lightMode = theme.palette.mode === "light";

  if ((COLORS as readonly string[]).includes(color)) {
    const palleteColor = theme.palette[color as PaletteColorKey];

    return {
      ...defaultSoft,
      color: palleteColor[lightMode ? "dark" : "light"],
      backgroundColor: alpha(palleteColor.main, 0.16),
      "&:hover": { backgroundColor: alpha(palleteColor.main, 0.32) },
    };
  }

  return {
    ...defaultSoft,
    color,
    backgroundColor: alpha(color, 0.16),
    "&:hover": { backgroundColor: alpha(color, 0.32) },
  };
};

export default function Chip(theme: Theme): { MuiChip: object } {
  const rootStyles = (ownerState: ChipProps): object[] => {
    const { color, variant } = ownerState;
    const defaultColor = color === "default";
    const softVariant = variant === "soft";

    const defaultStyle = {
      ...(defaultColor && {
        ...(softVariant && {
          ...defaultSoft,
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.grey[500], 0.16),
          "&:hover": { backgroundColor: alpha(theme.palette.grey[500], 0.32) },
        }),
      }),
    };

    const softColorStyle = !defaultColor && softVariant && color ? getPaletteColor(color, theme) : {};

    const disabledState = {
      [`&.${chipClasses.disabled}`]: {
        opacity: 1,
        color: theme.palette.action.disabled,
        ...(softVariant && { backgroundColor: theme.palette.action.disabledBackground }),
      },
    };

    return [defaultStyle, softColorStyle, disabledState];
  };

  return {
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ChipProps }) => rootStyles(ownerState),
        colorDefault: { "& .MuiChip-avatarMedium, .MuiChip-avatarSmall": { color: theme.palette.text.secondary } },
        outlined: {
          borderColor: theme.palette.grey[500_32],
          "&.MuiChip-colorPrimary": { borderColor: theme.palette.primary.main },
          "&.MuiChip-colorSecondary": { borderColor: theme.palette.secondary.main },
        },
      },
    },
  };
}
